import React, { useMemo, useState } from 'react'
import { Dropdown, Form, Input } from 'semantic-ui-react'
import * as R from 'ramda'

import { LinkButton } from 'elements/LinkButton'
import Popup from 'shared/components/Popup'
import produce from 'immer'
import { Loader } from 'shared/components/Loader'
import EasyToClickIcon from 'components/EasyToClickIcon'

function TeamMemberMappingRow({ value, options, onChange, onRemove, onAddItem, allMappedEmails }) {
  const [searchQuery, setSearchQuery] = useState('')

  const isDuplicateSearch =
    Boolean(searchQuery) &&
    allMappedEmails.some(
      (email) => email !== value && email.toLowerCase() === searchQuery.toLowerCase()
    )

  return (
    <Form.Group className="items-center">
      <Form.Field width={8}>
        <Dropdown
          search={(options, query) =>
            options.filter(
              (option) =>
                option?.text?.toLowerCase().includes(query.toLowerCase()) ||
                (option.searchName && option.searchName.toLowerCase().includes(query.toLowerCase()))
            )
          }
          selection
          fluid
          allowAdditions={!isDuplicateSearch}
          additionLabel="Add: "
          className="team-member-mapping-dropdown"
          placeholder="Search team member or enter an email"
          searchQuery={searchQuery}
          onSearchChange={(_, { searchQuery: q }) => setSearchQuery(q)}
          onOpen={() => setSearchQuery('')}
          onClose={() => setSearchQuery('')}
          options={options}
          value={value}
          onChange={onChange}
          onAddItem={(_, { value: v }) => onAddItem(v)}
          noResultsMessage={
            isDuplicateSearch ? 'This email already exists.' : undefined
          }
        />
      </Form.Field>
      <EasyToClickIcon className="icon fas fa-xmark link !ml-1" onClick={onRemove} />
    </Form.Group>
  )
}

/**
 *
 * @param {object} props
 * @param {boolean} props.loading
 * @param {string} props.fixedTeamMemberMapping
 * @param {string[]} props.defaultTeamMemberMapping
 * @param {function} props.onTeamMemberMappingChange
 * @param {Array<{email: string, name: string}>} props.teamMembers
 */
export default function TeamMemberMappingInput({
  loading,
  fixedTeamMemberMapping,
  defaultTeamMemberMapping,
  onTeamMemberMappingChange,
  teamMembers,
}) {
  const [prevDefaultMapping, setPrevDefaultMapping] = useState(defaultTeamMemberMapping)
  const [teamMemberMapping, setTeamMemberMapping] = useState(defaultTeamMemberMapping || [])
  const [sessionAdditions, setSessionAdditions] = useState([])

  if (prevDefaultMapping !== defaultTeamMemberMapping) {
    setPrevDefaultMapping(defaultTeamMemberMapping)
    setTeamMemberMapping(defaultTeamMemberMapping || [])
  }

  const teamMemberEmailSet = useMemo(
    () => new Set((teamMembers || []).map((member) => member.email)),
    [teamMembers]
  )

  const baseOptions = useMemo(
    () =>
      (teamMembers || [])
        .filter((member) => member.email)
        .map((member) => ({
          key: member.email,
          value: member.email,
          text: member.email,
          content: member.name ? (
            <div>
              <div>{member.name}</div>
              <div className="text-grey">{member.email}</div>
            </div>
          ) : (
            member.email
          ),
          searchName: member.name || '',
        })),
    [teamMembers]
  )

  const derivedAdditionalOptions = useMemo(() => {
    if (!defaultTeamMemberMapping) {
      return []
    }
    return defaultTeamMemberMapping
      .filter((email) => email && !teamMemberEmailSet.has(email))
      .map((email) => ({ key: email, value: email, text: email }))
  }, [defaultTeamMemberMapping, teamMemberEmailSet])

  const dropdownOptions = useMemo(
    () => [...baseOptions, ...derivedAdditionalOptions, ...sessionAdditions],
    [baseOptions, derivedAdditionalOptions, sessionAdditions]
  )

  function handleAddTeamMemberMappingRecord() {
    setTeamMemberMapping((current) => current.concat(''))
  }

  function handleAddItem(value) {
    if (!dropdownOptions.some((opt) => opt.value === value)) {
      setSessionAdditions((current) => [...current, { key: value, value, text: value }])
    }
  }

  return (
    <React.Fragment>
      <Form>
        {fixedTeamMemberMapping && (
          <Form.Group>
            <Form.Field width={8}>
              <Popup basic tooltip="Default value">
                <div>
                  <Input
                    fluid
                    placeholder="Team member email"
                    value={fixedTeamMemberMapping}
                    disabled
                  />
                </div>
              </Popup>
            </Form.Field>
          </Form.Group>
        )}
        {loading ? (
          <Loader withContainer />
        ) : (
          teamMemberMapping.map((teamMemberMappingRecord, index) => {
            function handleRemove() {
              setTeamMemberMapping((current) => {
                const newMapping = R.remove(index, 1, current)
                onTeamMemberMappingChange(newMapping.filter(R.identity))
                return newMapping
              })
            }

            function handleChange(_, { value }) {
              const isDuplicate = teamMemberMapping.some((email, i) => i !== index && email === value)
              if (isDuplicate) {
                return
              }
              setTeamMemberMapping((current) => {
                const updated = produce(current, (draft) => {
                  draft[index] = value
                })
                onTeamMemberMappingChange(updated.filter(R.identity))
                return updated
              })
            }

            if (teamMembers !== undefined) {
              const rowOptions = dropdownOptions.filter(
                (opt) => !teamMemberMapping.some((email, i) => i !== index && email === opt.value)
              )
              return (
                <TeamMemberMappingRow
                  key={index}
                  value={teamMemberMappingRecord}
                  options={rowOptions}
                  onChange={handleChange}
                  onRemove={handleRemove}
                  onAddItem={handleAddItem}
                  allMappedEmails={teamMemberMapping}
                />
              )
            }

            return (
              <Form.Group className="items-center" key={index}>
                <Form.Field width={8}>
                  <Input
                    fluid
                    placeholder="Team member email"
                    value={teamMemberMappingRecord}
                    onChange={handleChange}
                  />
                </Form.Field>
                <EasyToClickIcon className="icon fas fa-xmark link !ml-1" onClick={handleRemove} />
              </Form.Group>
            )
          })
        )}
      </Form>
      <LinkButton onClick={handleAddTeamMemberMappingRecord}>
        <i className="icon far fa-plus" /> Add team member mapping
      </LinkButton>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/TeamMemberMappingInput/index.js